import { useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageCircle, ChevronRight, Plus, Minus, Database, Sparkles, Users,
  Briefcase, Building2, Puzzle, BarChart3, Target, Layers, ShieldCheck,
  CheckCircle2, TrendingUp, Scale,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsapp from "@/components/FloatingWhatsapp";
import CookieBanner from "@/components/CookieBanner";
import { useSeo } from "@/hooks/useSeo";
import {
  DATOSIA_META, USE_CHIPS, DATA_TYPES, USE_CASES, STEPS, WHY_US,
  AI_TOOLS, FORMATS, DATOSIA_FAQS,
} from "@/pages/datosIaData";

const WA = "https://wa.me/56936400479?text=Hola%2C%20quisiera%20m%C3%A1s%20informaci%C3%B3n%20sobre%20datos%20masivos";

const TYPE_ICONS = [Users, Briefcase, Building2, Puzzle];

const WaButton = ({ label, testid, big }) => (
  <a
    href={WA}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className={`inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold rounded-full transition-all shadow-[0_12px_40px_-8px_rgba(37,211,102,0.5)] hover:-translate-y-0.5 ${
      big ? "text-base px-8 py-4" : "text-[15px] px-7 py-3.5"
    }`}
  >
    <MessageCircle size={big ? 20 : 18} />
    <span>{label}</span>
  </a>
);

const Overline = ({ children }) => (
  <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#00f0ff] mb-4">
    {children}
  </div>
);

const H2 = ({ children }) => (
  <h2 className="font-display font-black text-white text-3xl sm:text-4xl leading-[1.05] tracking-tight mb-6">
    {children}
  </h2>
);

const LegalNote = ({ children }) => (
  <div className="mt-6 flex items-start gap-3 rounded-xl border border-[rgba(0,240,255,0.15)] bg-[rgba(10,18,36,0.5)] p-5">
    <Scale className="text-[#00f0ff] shrink-0 mt-0.5" size={16} />
    <p className="text-[13px] text-[#94a3b8] leading-relaxed">{children}</p>
  </div>
);

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Datasets personalizados de datos en Chile",
    serviceType: "Venta de datasets personalizados para IA, estudios de mercado, analítica y marketing",
    provider: { "@type": "Organization", name: "Chile Datos Pro", url: "https://chiledatospro.cl/" },
    areaServed: { "@type": "Country", name: "Chile" },
    description: DATOSIA_META.description,
    offers: {
      "@type": "Offer",
      price: "299990",
      priceCurrency: "CLP",
      description: "200.000 registros personalizados desde $299.990 + IVA. Precio final sujeto a variables, disponibilidad y complejidad del proyecto.",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Inicio", item: "https://chiledatospro.cl/" },
      { "@type": "ListItem", position: 2, name: "Datos para IA", item: DATOSIA_META.canonical },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: DATOSIA_FAQS.map(([q, a]) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  },
];

const FaqList = () => {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-3">
      {DATOSIA_FAQS.map(([q, a], i) => {
        const isOpen = open === i;
        return (
          <div
            key={q}
            data-testid={`datosia-faq-item-${i}`}
            className={`rounded-2xl border transition-all overflow-hidden ${
              isOpen
                ? "border-[rgba(0,240,255,0.5)] bg-[rgba(0,240,255,0.04)]"
                : "border-[rgba(0,240,255,0.12)] bg-[rgba(10,18,36,0.4)]"
            }`}
          >
            <button
              onClick={() => setOpen(isOpen ? -1 : i)}
              data-testid={`datosia-faq-trigger-${i}`}
              className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
            >
              <span className="font-display font-semibold text-white text-[15px] sm:text-base leading-tight">{q}</span>
              <div className={`w-8 h-8 rounded-full border flex items-center justify-center shrink-0 transition-all ${
                isOpen ? "bg-[#00f0ff] border-[#00f0ff] text-[#040812]" : "border-[rgba(0,240,255,0.3)] text-[#00f0ff]"
              }`}>
                {isOpen ? <Minus size={14} /> : <Plus size={14} />}
              </div>
            </button>
            {isOpen && <div className="px-6 pb-6 text-[#94a3b8] text-[14px] leading-relaxed">{a}</div>}
          </div>
        );
      })}
    </div>
  );
};

export default function DatosIA() {
  useSeo(DATOSIA_META);

  return (
    <div className="App relative">
      {jsonLd.map((s, i) => (
        <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(s) }} />
      ))}
      <Navbar />
      <main data-testid="datosia-page">
        {/* ============ HERO ============ */}
        <section className="relative min-h-[92vh] pt-32 pb-16 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="/img-datos-ia-hero.jpg"
              alt="Red de datos conectada a un mapa de Chile para inteligencia artificial"
              className="w-full h-full object-cover opacity-25"
              loading="eager"
              fetchPriority="high"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#040812] via-[#040812]/80 to-[#040812]" />
            <div className="absolute inset-0 grid-pattern" />
            <div className="absolute inset-0 radial-glow" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
            <nav aria-label="breadcrumb" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#64748b] mb-8">
              <Link to="/" className="hover:text-[#00f0ff] transition-colors" data-testid="datosia-breadcrumb-home">Inicio</Link>
              <ChevronRight size={12} />
              <span className="text-[#00f0ff]">Datos para IA</span>
            </nav>
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[rgba(0,240,255,0.3)] bg-[rgba(0,240,255,0.06)] mb-8">
                  <Database size={13} className="text-[#00f0ff]" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#00f0ff]">
                    Tu partner de datos en Chile
                  </span>
                </div>
                <h1 className="font-display font-black text-white text-[38px] sm:text-5xl lg:text-6xl leading-[1] tracking-tight mb-6">
                  Datos para <span className="text-[#00f0ff] text-cyan-glow">IA</span>,
                  estudios de mercado y marketing en Chile
                </h1>
                <p className="text-[17px] text-[#cbd5e1] leading-relaxed max-w-xl mb-4 font-medium">
                  ¿Tu proyecto necesita datos? Nosotros te ayudamos a encontrarlos.
                </p>
                <p className="text-[15.5px] text-[#94a3b8] leading-relaxed max-w-xl mb-8">
                  Datos de personas, profesionales y empresas en Chile para
                  inteligencia artificial, estudios de mercado, analítica,
                  investigación y marketing. No necesitas comprar una base
                  genérica con información que nunca utilizarás. Cuéntanos qué
                  quieres hacer, qué universo necesitas analizar y qué variables
                  buscas. <span className="text-white font-semibold">Nosotros construimos un dataset a la medida de tu proyecto.</span>
                </p>
                <WaButton label="Cotizar mi dataset" testid="datosia-hero-cta" big />
              </div>
              <div className="lg:col-span-5">
                <div className="gradient-border relative overflow-hidden p-8 animate-float">
                  <div className="absolute inset-0 compliance-shimmer pointer-events-none" />
                  <div className="relative">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#00f0ff] mb-3">
                      Precio de referencia
                    </div>
                    <div className="font-display font-black text-white text-4xl sm:text-5xl leading-none mb-1" data-testid="datosia-price">
                      $299.990
                    </div>
                    <div className="font-mono text-[12px] text-[#94a3b8] uppercase tracking-wider mb-5">
                      Desde · + IVA
                    </div>
                    <div className="flex items-center gap-3 pb-5 mb-5 border-b border-[rgba(0,240,255,0.15)]">
                      <div className="w-11 h-11 rounded-xl bg-[rgba(0,240,255,0.1)] border border-[rgba(0,240,255,0.3)] flex items-center justify-center">
                        <Layers className="text-[#00f0ff]" size={20} />
                      </div>
                      <div>
                        <div className="font-display font-bold text-white text-lg leading-tight">200.000 registros</div>
                        <div className="text-[12px] text-[#94a3b8]">personalizados*</div>
                      </div>
                    </div>
                    <p className="text-[11px] text-[#64748b] leading-relaxed">
                      *Cantidad, variables, cobertura y precio final sujetos a
                      disponibilidad, fuente, finalidad y condiciones de
                      tratamiento de los datos.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ PARTNER ============ */}
        <section className="relative py-24 border-t border-[rgba(0,240,255,0.08)]">
          <div className="max-w-5xl mx-auto px-5 sm:px-8">
            <Overline>/ 01 — Partner de data</Overline>
            <H2>
              Más que vender datos: queremos ser tu{" "}
              <span className="text-[#00f0ff]">partner de DATA</span>
            </H2>
            <div className="grid md:grid-cols-2 gap-10 items-start">
              <div className="space-y-4 text-[15.5px] text-[#94a3b8] leading-relaxed">
                <p>
                  Hoy las empresas tienen acceso a mejores herramientas de
                  inteligencia artificial, automatización y análisis que nunca.
                  Pero existe un elemento que sigue siendo fundamental:{" "}
                  <span className="text-white font-semibold">los datos.</span>
                </p>
                <p>
                  Una herramienta de IA sin información adecuada no conoce
                  necesariamente el mercado específico que quieres analizar. Un
                  estudio sin información suficiente puede comenzar desde una
                  hipótesis equivocada. Una campaña sin una segmentación
                  adecuada desperdicia recursos.
                </p>
                <p>
                  Por eso ChileDatosPro busca ir más allá de la venta
                  tradicional de bases de datos.{" "}
                  <span className="text-white font-semibold">
                    Tú nos explicas el problema. Nosotros estudiamos los datos
                    que necesitas.
                  </span>
                </p>
              </div>
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#94a3b8] mb-4">
                  Podemos ayudarte a construir datasets para:
                </div>
                <div className="flex flex-wrap gap-2.5">
                  {USE_CHIPS.map((c) => (
                    <span
                      key={c}
                      className="px-4 py-2 rounded-full border border-[rgba(0,240,255,0.25)] bg-[rgba(0,240,255,0.05)] text-[13px] text-[#cbd5e1] hover:border-[#00f0ff] hover:text-white transition-colors"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ TIPOS DE DATOS ============ */}
        <section className="relative py-24 border-t border-[rgba(0,240,255,0.08)]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
            <div className="text-center mb-14">
              <Overline>/ 02 — Universos disponibles</Overline>
              <H2>¿Qué tipo de datos podemos trabajar?</H2>
              <p className="text-[15px] text-[#94a3b8] max-w-3xl mx-auto leading-relaxed">
                Dependiendo del universo solicitado, disponibilidad de las
                fuentes, actualidad de la información y finalidad permitida,
                podemos construir segmentos utilizando diferentes variables.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {DATA_TYPES.map((d, idx) => {
                const Icon = TYPE_ICONS[idx];
                return (
                  <div
                    key={d.title}
                    data-testid={`datosia-type-${idx}`}
                    className={`rounded-2xl border p-6 transition-all hover:-translate-y-1 ${
                      d.highlight
                        ? "border-[rgba(0,240,255,0.5)] bg-[rgba(0,240,255,0.06)]"
                        : "border-[rgba(0,240,255,0.15)] bg-[rgba(10,18,36,0.4)] hover:border-[rgba(0,240,255,0.4)]"
                    }`}
                  >
                    <div className="w-11 h-11 rounded-xl bg-[rgba(0,240,255,0.1)] border border-[rgba(0,240,255,0.3)] flex items-center justify-center mb-4">
                      <Icon className="text-[#00f0ff]" size={20} />
                    </div>
                    <h3 className="font-display font-bold text-white text-lg mb-4">{d.title}</h3>
                    <ul className="space-y-2">
                      {d.items.map((it) => (
                        <li key={it} className="flex items-start gap-2 text-[13px] text-[#94a3b8] leading-snug">
                          <span className="mt-[7px] w-1 h-1 rounded-full bg-[#00f0ff] shrink-0" />
                          {it}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ============ 18 MILLONES ============ */}
        <section className="relative py-24 border-t border-[rgba(0,240,255,0.08)] overflow-hidden">
          <div className="absolute inset-0 radial-glow" />
          <div className="relative max-w-5xl mx-auto px-5 sm:px-8 text-center">
            <Overline>/ 03 — Volumen y segmentación</Overline>
            <h2 className="font-display font-black text-white text-3xl sm:text-5xl leading-[1.05] tracking-tight mb-6">
              Más de <span className="text-[#00f0ff] text-cyan-glow">18 millones</span> de
              registros disponibles para segmentación
            </h2>
            <p className="text-[15.5px] text-[#94a3b8] leading-relaxed max-w-2xl mx-auto mb-10">
              La cantidad no es lo más importante.{" "}
              <span className="text-white font-semibold">
                Lo importante es encontrar los datos correctos dentro de ella.
              </span>{" "}
              Por eso trabajamos sobre grandes volúmenes de información para
              construir segmentos específicos según cada proyecto.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto mb-10">
              {[["200.000", "registros desde $299.990 + IVA"], ["500.000", "registros — cotización personalizada"], ["1.000.000+", "proyectos de mayor escala"]].map(([n, t]) => (
                <div key={n} className="rounded-2xl border border-[rgba(0,240,255,0.15)] bg-[rgba(10,18,36,0.5)] p-6">
                  <div className="font-display font-black text-2xl text-[#00f0ff]">{n}</div>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-[#94a3b8] mt-2">{t}</div>
                </div>
              ))}
            </div>
            <WaButton label="Quiero cotizar mi proyecto" testid="datosia-volume-cta" big />
          </div>
        </section>

        {/* ============ DATOS PARA IA ============ */}
        <section className="relative py-24 border-t border-[rgba(0,240,255,0.08)] overflow-hidden">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Overline>/ 04 — Datos para Inteligencia Artificial</Overline>
                <H2>
                  La IA tiene el poder.{" "}
                  <span className="text-[#00f0ff]">Los datos le entregan contexto.</span>
                </H2>
                <p className="text-[15.5px] text-[#94a3b8] leading-relaxed mb-5">
                  ChileDatosPro puede suministrar datasets estructurados para
                  proyectos donde los datos sean parte del análisis, validación,
                  enriquecimiento o funcionamiento de una solución basada en IA.
                </p>
                <p className="text-[15.5px] text-[#94a3b8] leading-relaxed mb-6">
                  No todos los proyectos necesitan millones de registros. Y más
                  datos no significan automáticamente una mejor IA. Por eso
                  primero necesitamos entender:
                </p>
                <ul className="space-y-3">
                  {["¿Qué quieres construir?", "¿Qué quieres predecir, analizar o clasificar?", "¿Qué población necesitas estudiar?", "¿Qué variables podrían ser relevantes?"].map((q) => (
                    <li key={q} className="flex items-start gap-3 text-[15px] text-[#cbd5e1]">
                      <Sparkles className="text-[#00f0ff] shrink-0 mt-0.5" size={16} />
                      {q}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative">
                <div className="absolute -inset-6 bg-[#00f0ff]/10 rounded-3xl blur-3xl" />
                <img
                  src="/img-datos-ia-casos.jpg"
                  alt="Dashboard de inteligencia de mercado con IA y datos de Chile"
                  className="relative rounded-2xl border border-[rgba(0,240,255,0.2)] w-full"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ============ CASOS DE USO ============ */}
        <section className="relative py-24 border-t border-[rgba(0,240,255,0.08)]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
            <div className="text-center mb-14">
              <Overline>/ 05 — Ideas de negocio</Overline>
              <H2>¿Qué puedes construir con datos + IA?</H2>
              <p className="text-[15px] text-[#94a3b8]">Aquí comienza la parte interesante.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {USE_CASES.map(([title, desc], i) => (
                <div
                  key={title}
                  data-testid={`datosia-usecase-${i}`}
                  className="rounded-2xl border border-[rgba(0,240,255,0.15)] bg-[rgba(10,18,36,0.4)] p-6 hover:border-[rgba(0,240,255,0.4)] transition-all hover:-translate-y-1"
                >
                  <div className="font-mono text-[#00f0ff] text-sm mb-3">0{i + 1}</div>
                  <h3 className="font-display font-bold text-white text-[15px] leading-tight mb-3">{title}</h3>
                  <p className="text-[13px] text-[#94a3b8] leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ IDEA CTA ============ */}
        <section className="relative py-20 border-t border-[rgba(0,240,255,0.08)]">
          <div className="max-w-5xl mx-auto px-5 sm:px-8">
            <div data-testid="datosia-idea-cta" className="gradient-border relative overflow-hidden p-8 sm:p-12 text-center">
              <div className="absolute inset-0 radial-glow pointer-events-none" />
              <div className="relative">
                <Overline>¿Tienes una idea de negocio con IA?</Overline>
                <h2 className="font-display font-black text-white text-2xl sm:text-4xl leading-tight mb-5">
                  Tú tienes la idea.{" "}
                  <span className="text-[#00f0ff] text-cyan-glow">Nosotros los datos.</span>
                </h2>
                <p className="text-[15px] text-[#94a3b8] leading-relaxed max-w-2xl mx-auto mb-8">
                  No necesitas llegar con toda la arquitectura resuelta.
                  Cuéntanos qué quieres construir, a quién quieres analizar, qué
                  información necesitas y qué resultado esperas obtener. Podemos
                  ayudarte a evaluar qué dataset podría ser útil para comenzar.
                </p>
                <WaButton label="Conversemos sobre tu proyecto" testid="datosia-idea-cta-whatsapp" big />
              </div>
            </div>
          </div>
        </section>

        {/* ============ ESTUDIOS + MARKETING ============ */}
        <section className="relative py-24 border-t border-[rgba(0,240,255,0.08)]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-10">
            <div className="rounded-3xl border border-[rgba(0,240,255,0.15)] bg-[rgba(10,18,36,0.4)] p-8 sm:p-10">
              <div className="w-12 h-12 rounded-xl bg-[rgba(0,240,255,0.1)] border border-[rgba(0,240,255,0.3)] flex items-center justify-center mb-5">
                <BarChart3 className="text-[#00f0ff]" size={22} />
              </div>
              <h2 className="font-display font-black text-white text-2xl sm:text-3xl leading-tight mb-4">
                Datos para estudios de mercado y estadística
              </h2>
              <p className="text-[14.5px] text-[#94a3b8] leading-relaxed mb-5">
                Desarrollamos datasets para empresas, consultoras e
                investigadores que necesitan estudiar segmentos específicos del
                mercado chileno:
              </p>
              <ul className="space-y-2 mb-4">
                {["Distribución territorial de empresas", "Análisis por actividad económica", "Segmentación demográfica", "Estudios de determinadas profesiones", "Análisis del parque automotriz", "Investigación inmobiliaria", "Comparaciones regionales", "Análisis exploratorio de mercados"].map((x) => (
                  <li key={x} className="flex items-start gap-2.5 text-[13.5px] text-[#cbd5e1]">
                    <CheckCircle2 className="text-[#00f0ff] shrink-0 mt-0.5" size={14} />
                    {x}
                  </li>
                ))}
              </ul>
              <LegalNote>
                Importante: disponer de muchos registros no convierte
                automáticamente una base en una muestra estadísticamente
                representativa. Si el objetivo es inferencia estadística, deben
                evaluarse metodología, cobertura, sesgos y diseño muestral.
              </LegalNote>
            </div>
            <div className="rounded-3xl border border-[rgba(0,240,255,0.15)] bg-[rgba(10,18,36,0.4)] p-8 sm:p-10">
              <div className="w-12 h-12 rounded-xl bg-[rgba(0,240,255,0.1)] border border-[rgba(0,240,255,0.3)] flex items-center justify-center mb-5">
                <Target className="text-[#00f0ff]" size={22} />
              </div>
              <h2 className="font-display font-black text-white text-2xl sm:text-3xl leading-tight mb-4">
                Datos para marketing y prospección
              </h2>
              <p className="text-[14.5px] text-[#94a3b8] leading-relaxed mb-5">
                Llega a un mercado más específico. Construimos bases destinadas
                a acciones comerciales cuando la procedencia y finalidad de los
                datos permiten ese uso. En lugar de comprar cientos de miles de
                registros indiscriminadamente, define el segmento que realmente
                interesa a tu empresa:
              </p>
              <ul className="space-y-2 mb-4">
                {["Prospección B2B", "Investigación comercial", "Generación de audiencias", "Campañas de email", "Campañas masivas autorizadas", "Análisis previo a campañas", "Enriquecimiento de CRM", "Inteligencia comercial"].map((x) => (
                  <li key={x} className="flex items-start gap-2.5 text-[13.5px] text-[#cbd5e1]">
                    <CheckCircle2 className="text-[#00f0ff] shrink-0 mt-0.5" size={14} />
                    {x}
                  </li>
                ))}
              </ul>
              <LegalNote>
                La disponibilidad de un dato no significa que pueda utilizarse
                para cualquier finalidad. Antes de campañas comerciales,
                especialmente con datos personales, debe evaluarse la base
                jurídica, origen, finalidad y normativa aplicable.
              </LegalNote>
            </div>
          </div>
        </section>

        {/* ============ ENRIQUECIMIENTO ============ */}
        <section className="relative py-24 border-t border-[rgba(0,240,255,0.08)] overflow-hidden">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="relative order-2 lg:order-1">
                <div className="absolute -inset-6 bg-[#00f0ff]/10 rounded-3xl blur-3xl" />
                <img
                  src="/img-datos-ia-enriquecimiento.jpg"
                  alt="Enriquecimiento de bases de datos con variables adicionales"
                  className="relative rounded-2xl border border-[rgba(0,240,255,0.2)] w-full"
                  loading="lazy"
                />
              </div>
              <div className="order-1 lg:order-2">
                <Overline>/ 06 — Enriquecimiento de datos</Overline>
                <H2>
                  ¿Ya tienes una base de datos?{" "}
                  <span className="text-[#00f0ff]">No compres otra. Enriquécela.</span>
                </H2>
                <p className="text-[15.5px] text-[#94a3b8] leading-relaxed mb-5">
                  Si tu empresa posee una base propia, podemos evaluar qué
                  variables adicionales pueden incorporarse legítimamente. Esto
                  puede ayudar a convertir{" "}
                  <span className="text-white font-semibold">50.000 registros con poca información</span>{" "}
                  en{" "}
                  <span className="text-[#00f0ff] font-semibold">50.000 registros con más contexto para análisis</span>.
                </p>
                <div className="flex flex-wrap gap-2 mb-8">
                  {["Segmentación", "BI", "CRM", "Analítica", "IA", "Estudios de clientes", "Inteligencia comercial"].map((c) => (
                    <span key={c} className="px-3.5 py-1.5 rounded-full border border-[rgba(0,240,255,0.25)] bg-[rgba(0,240,255,0.05)] text-[12.5px] text-[#cbd5e1]">
                      {c}
                    </span>
                  ))}
                </div>
                <WaButton label="Cotizar enriquecimiento" testid="datosia-enrich-cta" />
              </div>
            </div>
          </div>
        </section>

        {/* ============ CÓMO FUNCIONA ============ */}
        <section className="relative py-24 border-t border-[rgba(0,240,255,0.08)]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
            <div className="text-center mb-14">
              <Overline>/ 07 — Proceso</Overline>
              <H2>¿Cómo funciona?</H2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
              {STEPS.map(([title, desc], i) => (
                <div key={title} data-testid={`datosia-step-${i}`} className="rounded-2xl border border-[rgba(0,240,255,0.15)] bg-[rgba(10,18,36,0.4)] p-6">
                  <div className="w-9 h-9 rounded-full bg-[#00f0ff] text-[#040812] font-display font-black text-sm flex items-center justify-center mb-4">
                    {i + 1}
                  </div>
                  <h3 className="font-display font-bold text-white text-[15px] mb-2">{title}</h3>
                  <p className="text-[13px] text-[#94a3b8] leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ PRECIO CTA ============ */}
        <section className="relative py-20 border-t border-[rgba(0,240,255,0.08)] overflow-hidden">
          <div className="absolute inset-0 radial-glow" />
          <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
            <Overline>Precio de referencia</Overline>
            <h2 className="font-display font-black text-white text-4xl sm:text-6xl leading-none tracking-tight mb-3">
              Desde <span className="text-[#00f0ff] text-cyan-glow">$299.990</span>
              <span className="text-2xl sm:text-3xl text-[#94a3b8]"> + IVA</span>
            </h2>
            <div className="font-mono text-[13px] uppercase tracking-[0.2em] text-[#94a3b8] mb-6">
              200.000 registros personalizados
            </div>
            <p className="text-[15px] text-[#94a3b8] leading-relaxed max-w-2xl mx-auto mb-8">
              No tienes que comprar millones de registros para comenzar.
              Construye un dataset de acuerdo con las necesidades reales de tu
              proyecto. Para requerimientos especiales, enriquecimiento, grandes
              volúmenes o procesamiento adicional realizamos una cotización
              personalizada. <span className="text-white font-semibold">¿Qué datos necesitas?</span>
            </p>
            <WaButton label="Cotizar ahora" testid="datosia-price-cta" big />
          </div>
        </section>

        {/* ============ POR QUÉ ============ */}
        <section className="relative py-24 border-t border-[rgba(0,240,255,0.08)]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
            <div className="text-center mb-14">
              <Overline>/ 08 — Diferencia</Overline>
              <H2>¿Por qué ChileDatosPro?</H2>
              <p className="text-[15px] text-[#94a3b8] max-w-2xl mx-auto leading-relaxed">
                Porque no queremos competir solamente por quién vende más
                registros.{" "}
                <span className="text-white font-semibold">
                  Queremos competir por quién entiende mejor lo que el cliente
                  necesita hacer con ellos.
                </span>
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {WHY_US.map(([title, desc], i) => {
                const icons = [Puzzle, Database, TrendingUp, Target, Layers, ShieldCheck];
                const Icon = icons[i];
                return (
                  <div key={title} className="rounded-2xl border border-[rgba(0,240,255,0.15)] bg-[rgba(10,18,36,0.4)] p-6 hover:border-[rgba(0,240,255,0.4)] transition-colors">
                    <Icon className="text-[#00f0ff] mb-4" size={22} />
                    <h3 className="font-display font-bold text-white text-base mb-2">{title}</h3>
                    <p className="text-[13.5px] text-[#94a3b8] leading-relaxed">{desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ============ COMPATIBILIDAD ============ */}
        <section className="relative py-24 border-t border-[rgba(0,240,255,0.08)]">
          <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center">
            <Overline>/ 09 — Compatibilidad</Overline>
            <H2>Datos compatibles con tus herramientas</H2>
            <p className="text-[15px] text-[#94a3b8] max-w-2xl mx-auto leading-relaxed mb-12">
              Entregamos datasets estructurados que puedes analizar y conectar
              con las principales plataformas de IA y herramientas de análisis,
              según la arquitectura de tu proyecto.
            </p>
            <div data-testid="datosia-ai-tools" className="grid grid-cols-3 sm:grid-cols-6 gap-4 mb-10">
              {AI_TOOLS.map(([name, icon]) => (
                <div key={name} className="flex flex-col items-center gap-3 rounded-2xl border border-[rgba(0,240,255,0.15)] bg-[rgba(10,18,36,0.4)] p-5 hover:border-[rgba(0,240,255,0.5)] transition-all hover:-translate-y-1">
                  <img src={icon} alt={`Icono de ${name}`} className="w-8 h-8" loading="lazy" />
                  <span className="font-mono text-[11px] uppercase tracking-wider text-[#cbd5e1]">{name}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap justify-center gap-2.5">
              {FORMATS.map((f) => (
                <span key={f} className="px-4 py-2 rounded-full border border-[rgba(0,240,255,0.25)] bg-[rgba(0,240,255,0.05)] font-mono text-[12px] uppercase tracking-wider text-[#00f0ff]">
                  {f}
                </span>
              ))}
            </div>
            <p className="text-[11px] text-[#64748b] mt-8 max-w-xl mx-auto">
              Las marcas mencionadas pertenecen a sus respectivos propietarios.
              La integración con cada herramienta depende de la arquitectura,
              condiciones del proveedor y naturaleza de la información.
            </p>
          </div>
        </section>

        {/* ============ FAQ ============ */}
        <section className="relative py-24 border-t border-[rgba(0,240,255,0.08)]">
          <div className="max-w-5xl mx-auto px-5 sm:px-8">
            <div className="text-center mb-12">
              <Overline>/ 10 — Preguntas frecuentes</Overline>
              <H2>Preguntas frecuentes sobre datos e IA</H2>
            </div>
            <FaqList />
          </div>
        </section>

        {/* ============ FINAL CTA ============ */}
        <section className="relative py-24 border-t border-[rgba(0,240,255,0.08)] overflow-hidden">
          <div className="absolute inset-0 grid-pattern opacity-40" />
          <div className="absolute inset-0 radial-glow" />
          <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#00f0ff] mb-5">
              ChileDatosPro — Los datos detrás de mejores decisiones
            </div>
            <h2 className="font-display font-black text-white text-3xl sm:text-5xl leading-[1.05] tracking-tight mb-6">
              No empieces preguntando{" "}
              <span className="italic font-light text-[#94a3b8]">“¿cuántos datos puedo comprar?”</span>
              <br />
              Empieza con:{" "}
              <span className="text-[#00f0ff] text-cyan-glow">“¿qué datos necesita mi proyecto?”</span>
            </h2>
            <p className="text-[15.5px] text-[#94a3b8] leading-relaxed max-w-xl mx-auto mb-9">
              IA, analítica, investigación, estudios de mercado o marketing.
              Cuéntanos qué quieres construir. Nosotros te ayudamos a encontrar
              los datos.
            </p>
            <WaButton label="Cotizar mi dataset" testid="datosia-final-cta" big />
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#64748b] mt-8">
              ChileDatosPro — Tu partner de datos en Chile
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingWhatsapp />
      <CookieBanner />
    </div>
  );
}
